import React, {useEffect, useMemo, useRef} from 'react';
import {
  ProForm,
  ProFormDigitRange,
  ProFormProps,
  ProFormSelect,
  ProFormSwitch,
  ProFormText,
  ProFormTextArea,
} from '@ant-design/pro-components';
import {EXTRA_FIELD_VALUE_TYPES} from '@/components/ExtraFields/types';
import {ExtraFieldTypeModel} from '@/typings/models/ExtraFields';

import styles from './index.less';
import get from 'lodash/get';
import classNames from 'classnames';
import {removeExtraFieldValueRequest, setExtraFieldValueRequest} from '@/services/api/extraFieldType';
import {useDispatch, useSelector} from 'umi';
import * as LeadSelectors from '@/selectors/leadSelectors';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import {Button, Flex, Popconfirm, Space, Spin} from 'antd';
import {CheckOutlined, CloseOutlined} from '@ant-design/icons';
import {ProFormDatePicker, ProFormDigit} from '@ant-design/pro-form';
import {getEntityConfig} from './entityConfig';
import {normalizeExtraFieldRequestValue} from '@/utils/extraFields';
import CodeEditor from '@uiw/react-textarea-code-editor';
import dayjs from 'dayjs';
import {F, INTL_ACTION, INTL_ENTITY} from "@/utils/intl";
import {useOverflowTooltip} from '@/utils/hooks';

const TRUE_VALUES = ['1', true, 'true'];
export const getFieldInitialValue = (field: ExtraFieldTypeModel) => {
  const { field_type, values } = field;

  switch (field_type) {
    case EXTRA_FIELD_VALUE_TYPES.JSON: {
      try {
        return JSON.stringify(get(values, '[0].value'), null, 4);
      } catch (error) {
        break;
      }
    }
    case EXTRA_FIELD_VALUE_TYPES.TEXT:
    case EXTRA_FIELD_VALUE_TYPES.NUMBER:
    case EXTRA_FIELD_VALUE_TYPES.TEXTAREA:
      return get(values, '[0].value', get(values, '[0].label'));
    case EXTRA_FIELD_VALUE_TYPES.SELECT:
      return get(values, '[0].option.uuid', get(values, '[0].option_uuid'));
    case EXTRA_FIELD_VALUE_TYPES.MULTI_SELECT:
      return values?.length > 0 ? values.map(value => get(value, 'option.uuid', get(value, 'option_uuid'))).filter(Boolean) : [];
    case EXTRA_FIELD_VALUE_TYPES.BOOL:
      return TRUE_VALUES.includes(get(values, '[0].value')) || TRUE_VALUES.includes(get(values, '[0].label')) || TRUE_VALUES.includes(get(values, '[0].option_uuid'));
    case EXTRA_FIELD_VALUE_TYPES.NUMBER_RANGE:
      try {
        return JSON.parse(get(values, '[0].value', '[]'));
      } catch (e) {
        return [];
      }
    case EXTRA_FIELD_VALUE_TYPES.DATE:
      return get(values, '[0].value', get(values, '[0].label'));
    default:
      return '';
  }
};

const FieldLabel = ({
  label,
  removable,
  onRemove,
  uuid,
}: {
  label: React.ReactNode;
  removable?: boolean;
  onRemove?: (fieldUuid: string) => void;
  uuid: string;
}) => {
  const overflowLabel = useOverflowTooltip(label);

  if (!removable) return <>{label}</>;

  return (
    <Flex gap={6} align={'center'} className={styles.removableFieldLabel}>
      <div className={styles.removableFieldLabelText}>{overflowLabel}</div>
      <div className={styles.removeExtraFieldItemAction}>
        <Popconfirm
          title={<F action={INTL_ACTION.REMOVE_CONFIRMATION} entity={INTL_ENTITY.EXTRA_FIELD} />}
          onConfirm={() => onRemove?.(uuid)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="text" size="small" icon={<CloseOutlined />} />
        </Popconfirm>
      </div>
    </Flex>
  );
};

export const ExtraFieldItem = React.memo(({
                                            field,
                                            calendarEvent,
                                            readonly,
                                            serviceRequestId,
                                            leadId,
                                            grid,
                                            branchId,
                                            branchServiceId,
                                            serviceTypeId,
                                            isInline = false,
                                            hideLabel = false,
                                            onChange,
                                            value,
                                            keyField,
                                            removable = false,
                                            onRemove,
                                            values: propValues,
                                          }: {
  field: ExtraFieldTypeModel,
  leadId?: string,
  serviceRequestId?: string,
  branchId?: string,
  keyField?: string,
  branchServiceId?: string,
  serviceTypeId?: string,
  isInline?: boolean,
  grid?: boolean,
  hideLabel?: boolean
  readonly?: boolean
  removable?: boolean
  onRemove?: (fieldUuid: string) => void
  values?: any[]
}) => {
  const { uuid, label, field_type, options, description } = field;
  const lead = useSelector((state: DefaultRootState) => LeadSelectors.selectLeadData(state, leadId));
  const dispatch = useDispatch();

  // Get entity configuration based on provided props
  const entityConfigResult = getEntityConfig({ leadId, serviceRequestId, branchId, branchServiceId, serviceTypeId }, field.related_to);
  const subjectId = entityConfigResult?.entityId || serviceRequestId || leadId;

  const [localValue, setLocalValue] = React.useState(getFieldInitialValue(field));
  const [loading, setLoading] = React.useState(false);
  const [lastSentValue, setLastSentValue] = React.useState(null);
  const [jsonError, setJsonError] = React.useState<string | null>(null);

  const values = propValues || lead?.extra_fields?.[field.uuid]?.values || field.values || calendarEvent?.extra_fields?.[field.uuid]?.values || [];

  const initialValue = useMemo(() => getFieldInitialValue({
    ...field,
    values,
  }), [values, field]);

  useEffect(() => {
    if (value)
      setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEqual(localValue, initialValue)) return;

    setLocalValue(initialValue);
  }, [initialValue]);

  const inlineValueProps = isInline ? { value: localValue, localValue, checked: localValue } : {};

  const validateJSON = (value: string): boolean => {
    if (!value || value.trim() === '') {
      setJsonError(null);
      return true;
    }

    try {
      JSON.parse(value);
      setJsonError(null);
      return true;
    } catch (error) {
      setJsonError('Invalid JSON format');
      return false;
    }
  };

  const handleFieldChange = (newValue: any) => {
    setLocalValue(newValue);
    if (onChange) onChange(newValue);

    // Return early if no entity is configured
    if (!entityConfigResult)
      return;

    // Only send request if the value has actually changed from the last sent value or initial value
    if (isEqual(newValue, lastSentValue ?? initialValue)) {
      return;
    }

    setLoading(true);
    setExtraFieldValueRequest({
      params: {
        extraFieldTypeId: field.uuid,
        leadId: subjectId,
      },
      data: {
        // Only the wire value is normalized; lastSentValue keeps the raw one.
        value: normalizeExtraFieldRequestValue(newValue),
      },
    }).then(() => {
      setLastSentValue(newValue);
      setLoading(false);

      // Unified dispatch using entity configuration
      const { entityConfig, entityId } = entityConfigResult;
      if (entityConfig.dispatchType)
        dispatch({
          type: entityConfig.dispatchType,
          payload: {
            [entityConfig.payloadIdKey]: entityId,
            fieldType: field_type,
            extraFieldTypeId: field.uuid,
            value: newValue,
          },
        });
    });
  };

  const variant = isInline ? 'borderless' : 'underlined';
  const placeholder = description || undefined;

  // Common field properties
  const getCommonFieldProps = () => ({
    key: uuid,
    name: field[keyField],
    label: hideLabel ? null : (
      <FieldLabel label={label} removable={removable} onRemove={onRemove} uuid={uuid} />
    ),
    allowClear: true,
    readonly,
    loading,
  });

  const getCommonEventHandlers = () => ({
    onChange: (e: any) => setLocalValue(e.target?.value ?? e),
    onClick: (e: any) => !readonly && e.stopPropagation(),
    onBlur: (e: any) => handleFieldChange(e.target?.value ?? e),
  });

  const getTextFieldProps = () => ({
    ...getCommonEventHandlers(),
    placeholder,
    variant,
    allowClear: true,
    ...inlineValueProps,
    size: 'small' as const,
  });

  // Individual field renderers
  const renderTextField = () => (
    <ProFormText
      allowClear
      {...getCommonFieldProps()}
      fieldProps={getTextFieldProps()}
    />
  );

  const renderTextAreaField = () => (
    <ProFormTextArea
      allowClear
      {...getCommonFieldProps()}
      fieldProps={{
        ...getTextFieldProps(),
        rows: 2,
      }}
    />
  );

  const renderSelectField = () => (
    <ProFormSelect
      {...getCommonFieldProps()}
      optionRender={(opt) => opt.label}
      fieldProps={{
        placeholder,
        variant,
        allowClear: true,
        ...inlineValueProps,
        popupMatchSelectWidth: false,
        size: 'small' as const,
        onClick: (e: any) => !readonly && e.stopPropagation(),
      }}
      onChange={handleFieldChange}
      options={options.map((opt) => ({
        label: opt.value,
        value: opt.uuid,
      }))}
    />
  );

  const renderMultiSelectField = () => (
    <ProFormSelect
      {...getCommonFieldProps()}
      mode="multiple"
      onChange={handleFieldChange}
      fieldProps={{
        placeholder,
        variant,
        allowClear: true,
        popupMatchSelectWidth: false,
        ...inlineValueProps,
        size: 'small' as const,
        onClick: (e: any) => !readonly && e.stopPropagation(),
      }}
      options={options.map((opt) => ({
        label: opt.value,
        value: opt.uuid,
      }))}
    />
  );

  const renderBoolField = () => (
    <ProFormSwitch
      {...getCommonFieldProps()}
      fieldProps={{
        ...inlineValueProps,
        size: 'small' as const,
        onChange: (checked: boolean) => handleFieldChange(checked ? '1' : '0'),
      }}
    />
  );

  const renderNumberField = () => (
    <ProFormDigit
      {...getCommonFieldProps()}
      fieldProps={{
        placeholder,
        variant,
        ...inlineValueProps,
        size: 'small' as const,
        parser: (value: string | undefined) => value?.replace(',', '.') ?? '',
        onChange: (value: any) => setLocalValue(value),
        onClick: (e: any) => !readonly && e.stopPropagation(),
        // InputNumber's onBlur receives the FocusEvent, not the number.
        onBlur: () => handleFieldChange(localValue),
      }}
    />
  );

  const renderNumberRangeField = () => (
    <ProFormDigitRange
      {...getCommonFieldProps()}
      fieldProps={{
        placeholder,
        variant,
        ...inlineValueProps,
        size: 'small' as const,
        onChange: (value: any) => handleFieldChange(value),
        onClick: (e: any) => !readonly && e.stopPropagation(),
      }}
    />
  );

  const renderDateField = () => (
    <ProFormDatePicker
      {...getCommonFieldProps()}
      fieldProps={{
        placeholder,
        variant,
        ...inlineValueProps,
        size: 'small' as const,
        style: { width: '100%' },
        format: 'YYYY-MM-DD',
        onChange: (date: any) => {
          setLocalValue(date);
          const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : null;
          handleFieldChange(formattedDate);
        },
        onClick: (e: any) => !readonly && e.stopPropagation(),
      }}
    />
  );

  const renderJSONField = () => (
    <ProForm.Item
      {...getCommonFieldProps()}
      fieldProps={{
        ...getTextFieldProps(),
        rows: 2,
      }}
      validateStatus={jsonError ? 'error' : ''}
    >
      <CodeEditor
        padding={8}
        language={'JSON'}
        data-color-mode="light"
        onChange={(e) => setLocalValue(e.target?.value)}
        onBlur={(e) => {
          const value = e.target?.value;
          if (validateJSON(value)) {
            handleFieldChange(value);
          }
        }}
        style={{
          fontFamily: 'ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace',
          border: jsonError ? '1px solid red' : '',
        }}
      />
    </ProForm.Item>
  );

  const getField = () => {
    switch (field_type) {
      case EXTRA_FIELD_VALUE_TYPES.TEXT:
        return renderTextField();
      case EXTRA_FIELD_VALUE_TYPES.TEXTAREA:
        return renderTextAreaField();
      case EXTRA_FIELD_VALUE_TYPES.SELECT:
        return renderSelectField();
      case EXTRA_FIELD_VALUE_TYPES.MULTI_SELECT:
        return renderMultiSelectField();
      case EXTRA_FIELD_VALUE_TYPES.BOOL:
        return renderBoolField();
      case EXTRA_FIELD_VALUE_TYPES.NUMBER:
        return renderNumberField();
      case EXTRA_FIELD_VALUE_TYPES.NUMBER_RANGE:
        return renderNumberRangeField();
      case EXTRA_FIELD_VALUE_TYPES.DATE:
        return renderDateField();
      case EXTRA_FIELD_VALUE_TYPES.JSON:
        return renderJSONField();
      default:
        return null;
    }
  };

  if (readonly && (typeof localValue === 'boolean' || typeof value === 'boolean'))
    return <Space className={styles.readonlyEmptyField}>
      <span>{label}</span>
      <span>{(value || localValue) ? <CheckOutlined /> : <CloseOutlined />}</span>
    </Space>;


  if (readonly && isEmpty(value) && isEmpty(localValue)) return <Space className={styles.readonlyEmptyField}>
    <span>{label}</span>
    <span>-</span>
  </Space>;

  const fieldContent = (
    <div className={classNames(styles.extraFieldItem, {
      [styles.gridItem]: grid,
    })} onClick={(e) => !readonly && e.stopPropagation()}>
      <Spin size={'small'} spinning={loading || false}>
        {getField()}
      </Spin>
    </div>
  );


  return fieldContent;

});

const DynamicExtraFieldsForm: React.FC<{
  fields: ExtraFieldTypeModel[];
  leadId?: string;
  labelWidth?: number;
  keyField?: string;
  inline?: boolean;
  grid?: boolean;
  serviceRequestId?: string;
  branchId?: string;
  branchServiceId?: string;
  serviceTypeId?: string;
  className?: string;
  formProps?: Partial<ProFormProps>;
  onValuesChange?: (changedValues: any, allValues: any) => void;
  onValuesSubmit?: (fields) => void;
  onRemove?: (fieldUuid: string) => void;
  removable?: boolean;
  onRefresh?: () => void;
  onAfterRemove?: (fieldUuid: string) => void;
}> = ({
  fields,
  leadId,
  className,
  inline,
  keyField = 'slug',
  formProps,
  labelWidth,
  serviceRequestId,
  branchId,
  branchServiceId,
  serviceTypeId,
  onValuesChange,
  onRemove,
  onValuesSubmit,
  removable = false,
  grid = false,
  onRefresh,
  onAfterRemove,
}) => {
  const [form] = ProForm.useForm();

  const subjectId =
    serviceRequestId || leadId || branchId || branchServiceId || serviceTypeId;

  const getInitialValues = () => {
    const initialValues: Record<string, any> = {};

    fields?.forEach((field) => {
      const initialValue = getFieldInitialValue(field);
      if (initialValue !== undefined) {
        initialValues[field[keyField]] = initialValue;
      }
    });

    return initialValues;
  };

  const handleFieldChange = (field, value) => {
    if (!onValuesSubmit) return;
    onValuesSubmit?.({
      [field[keyField]]: value,
    });
  };
  const prevInitialValuesRef = useRef<Record<string, any> | null>(null);

  useEffect(() => {
    const initialValues = getInitialValues();
    form.resetFields();
    form.setFieldsValue(initialValues);
    prevInitialValuesRef.current = initialValues;
  }, [subjectId]);

  useEffect(() => {
    // Only sync fields whose initial value actually changed (e.g. refreshed from
    // the server); a full reset here would wipe unsaved input when a field is
    // added or removed while the user is still filling the form.
    const initialValues = getInitialValues();
    const prevInitialValues = prevInitialValuesRef.current || {};
    prevInitialValuesRef.current = initialValues;

    const changedValues: Record<string, any> = {};
    new Set([
      ...Object.keys(prevInitialValues),
      ...Object.keys(initialValues),
    ]).forEach((key) => {
      if (!isEqual(initialValues[key], prevInitialValues[key])) {
        changedValues[key] = initialValues[key];
      }
    });

    if (Object.keys(changedValues).length > 0) {
      form.setFieldsValue(changedValues);
    }
  }, [JSON.stringify(fields)]);

  if (!fields || fields?.length === 0) return null;

  const proFormProps: Partial<ProFormProps> = formProps || {};
  if (onValuesChange) {
    proFormProps.onValuesChange = onValuesChange;
  }

  // Set default layout if not provided in formProps
  if (!proFormProps.layout) {
    proFormProps.layout = inline ? 'inline' : 'horizontal';
  }

  const handleRemove = async (fieldUuid: string) => {
    if (!subjectId) {
      if (onRemove) onRemove(fieldUuid);
      return;
    }

    try {
      await removeExtraFieldValueRequest({
        params: {
          extraFieldTypeId: fieldUuid,
          leadId: subjectId,
        },
      });

      if (onRefresh) {
        onRefresh();
      }
      if (onAfterRemove) onAfterRemove(fieldUuid);
    } catch (error) {
      console.error('Error removing extra field:', error);
    }
  };

  return (
    <div
      style={{
        '--ant-form-label-width': `${labelWidth || 90}px`,
      }}
      className={classNames(styles.dynamicExtraFieldsFormContainer, {
        [styles.gridForm]: grid,
      })}
    >
      <ProForm form={form} colon={false} submitter={false} {...proFormProps}>
        {fields.map((field) => (
          <ExtraFieldItem
            field={field}
            keyField={keyField}
            grid={grid}
            key={field.uuid}
            serviceRequestId={serviceRequestId}
            leadId={leadId}
            branchId={branchId}
            branchServiceId={branchServiceId}
            serviceTypeId={serviceTypeId}
            removable={removable}
            onChange={(value) => handleFieldChange(field, value)}
            onRemove={removable ? handleRemove : undefined}
          />
        ))}
      </ProForm>
    </div>
  );
};

export default DynamicExtraFieldsForm;
